import React, { Component } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import moment from "moment";
import instance from "../../axios";
import Loader from "../../Layout/Loader";

const ENTERPRISE = {
  border: "#E5E7EB",
  headerBg: "#F8FAFC",
  headerText: "#334155",
  text: "#1E293B",
  muted: "#64748B",
  accent: "#1565C0",
  success: "#6FBD43",
};

export default class VehicleAssessmentModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      assessmentData: [],
      assessmentLoading: false,
      assessmentError: "",
    };
  }

  componentDidMount() {
    this.fetchAssessments();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.vehicle?.id !== this.props.vehicle?.id) {
      this.fetchAssessments();
    }
  }

  resolveVin = () => {
    const { vehicle } = this.props;
    return (
      vehicle?.vin ||
      vehicle?.attributes?.vin ||
      ""
    );
  };

  fetchAssessments = async () => {
    this.setState({
      assessmentLoading: true,
      assessmentError: "",
      assessmentData: [],
    });

    const vin = this.resolveVin();
    if (!vin) {
      this.setState({
        assessmentLoading: false,
        assessmentError: "VIN not found for this vehicle",
      });
      return;
    }

    try {
      const response = await instance({
        url: `/api/vehicleassessment/get?vin=${encodeURIComponent(vin)}`,
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      const rows = response?.data || [];
      this.setState({
        assessmentData: rows,
        assessmentLoading: false,
        assessmentError:
          response?.total > 0 ? "" : "No assessment found for this VIN",
      });
    } catch (error) {
      console.error("Error fetching vehicle assessment:", error);
      this.setState({
        assessmentLoading: false,
        assessmentError: "Unable to load vehicle assessment",
        assessmentData: [],
      });
    }
  };

  createVehicleAssessment = () => {
    const vin = this.resolveVin();
    if (!vin) {
      alert("VIN not found for this vehicle");
      return;
    }
    window.open(`/inspection-vehicle/${encodeURIComponent(vin)}`, "_blank");
  };

  openViewAssessment = (row) => {
    const vin = this.resolveVin();
    if (!vin || !row?.id) {
      alert(!vin ? "VIN not found for this vehicle" : "Assessment id not found");
      return;
    }
    window.open(
      `/inspection-vehicle/${encodeURIComponent(vin)}?mode=view&assessmentId=${row.id}`,
      "_blank",
    );
  };

  deleteVehicleAssessment = async (row) => {
    if (!row?.id) {
      alert("Assessment id not found");
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to delete this vehicle assessment?",
    );
    if (!confirmed) return;

    this.setState({ assessmentLoading: true, assessmentError: "" });
    try {
      await instance({
        url: `/api/vehicleassessment/${row.id}`,
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      const nextRows = (this.state.assessmentData || []).filter(
        (item) => item.id !== row.id,
      );
      this.setState({
        assessmentData: nextRows,
        assessmentLoading: false,
        assessmentError: nextRows.length
          ? ""
          : "No assessment found for this VIN",
      });
    } catch (error) {
      console.error("Error deleting vehicle assessment:", error);
      this.setState({ assessmentLoading: false });
      alert("Unable to delete vehicle assessment. Please try again.");
    }
  };

  formatBool = (value) => {
    if (value === true || value === "true" || value === 1 || value === "1") {
      return "Yes";
    }
    if (value === false || value === "false" || value === 0 || value === "0") {
      return "No";
    }
    return "-";
  };

  headCellSx = {
    whiteSpace: "nowrap",
    backgroundColor: ENTERPRISE.headerBg,
    color: ENTERPRISE.headerText,
    fontWeight: 600,
    fontSize: "0.72rem",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    py: 1.25,
    px: 1.25,
    borderBottom: `1px solid ${ENTERPRISE.border}`,
  };

  bodyCellSx = {
    py: 1.1,
    px: 1.25,
    fontSize: "0.8125rem",
    color: ENTERPRISE.text,
    borderBottom: `1px solid ${ENTERPRISE.border}`,
    whiteSpace: "nowrap",
  };

  render() {
    const { vehicle, onClose, translate } = this.props;
    const { assessmentData, assessmentLoading, assessmentError } = this.state;
    const loginuser = JSON.parse(localStorage.getItem("userInfoFront") || "{}");
    const isSuperAdmin = loginuser?.userType == -1;
    const plate =
      vehicle?.vehicleLicensePlate ||
      vehicle?.label ||
      vehicle?.vin ||
      "Vehicle";

    return (
      <Dialog
        open
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "10px",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            py: 1.5,
            px: 2,
            borderBottom: `1px solid ${ENTERPRISE.border}`,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1rem",
                color: ENTERPRISE.text,
              }}
            >
              {translate?.("vehicleAssessment") || "Vehicle Assessment"}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: ENTERPRISE.muted }}>
              {plate}
              {assessmentLoading
                ? " · Loading assessments…"
                : ` · ${assessmentData?.length || 0} record${
                    (assessmentData?.length || 0) === 1 ? "" : "s"
                  }`}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="contained"
              onClick={this.createVehicleAssessment}
              sx={{
                backgroundColor: ENTERPRISE.success,
                color: "white",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#5AA832",
                  boxShadow: "none",
                },
              }}
            >
              Create
            </Button>
            <IconButton onClick={onClose} size="small" aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {assessmentLoading ? (
            <Box sx={{ py: 4 }}>
              <Loader />
            </Box>
          ) : assessmentError ? (
            <Typography
              sx={{ textAlign: "center", py: 4, color: ENTERPRISE.muted }}
            >
              {assessmentError}
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: "70vh" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {[
                      "VIN",
                      "Plate",
                      "Contract #",
                      "Current Mileage",
                      "Last Oil Change",
                      "Next Oil Change",
                      "Tyre",
                      "Battery",
                      "DTC Engine",
                      "DTC Emission",
                      "DTC Brakes",
                      "Action",
                    ].map((label) => (
                      <TableCell
                        key={label}
                        align={label === "Action" ? "center" : "left"}
                        sx={this.headCellSx}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assessmentData.map((row, index) => (
                    <TableRow
                      key={row.id || index}
                      hover
                      sx={{
                        "&:hover": { backgroundColor: "#F8FAFC" },
                      }}
                    >
                      <TableCell sx={this.bodyCellSx}>
                        {row.vin || "-"}
                      </TableCell>
                      <TableCell sx={this.bodyCellSx}>
                        {row.plate || "-"}
                      </TableCell>
                      <TableCell sx={this.bodyCellSx}>
                        {row.contractNumber || "-"}
                      </TableCell>
                      <TableCell sx={this.bodyCellSx}>
                        {row.currentMileage != null
                          ? row.currentMileage
                          : "-"}
                      </TableCell>
                      <TableCell sx={this.bodyCellSx}>
                        {row.lastOilChangeMileage != null
                          ? `${row.lastOilChangeMileage}`
                          : "-"}
                        {row.lastOilChangeDate
                          ? ` / ${moment(row.lastOilChangeDate).format(
                              "DD-MM-YYYY",
                            )}`
                          : ""}
                      </TableCell>
                      <TableCell sx={this.bodyCellSx}>
                        {row.nextOilChangeMileage != null
                          ? `${row.nextOilChangeMileage}`
                          : "-"}
                        {row.nextOilChangeDate
                          ? ` / ${moment(row.nextOilChangeDate).format(
                              "DD-MM-YYYY",
                            )}`
                          : ""}
                      </TableCell>
                      <TableCell sx={this.bodyCellSx}>
                        {[row.tyreBrand, row.tyreSize]
                          .filter(Boolean)
                          .join(" ") || "-"}
                        {row.tyreExpDate
                          ? ` (exp ${moment(row.tyreExpDate).format(
                              "DD-MM-YYYY",
                            )})`
                          : ""}
                      </TableCell>
                      <TableCell sx={this.bodyCellSx}>
                        {[
                          row.batteryBrand,
                          row.batteryAmp != null
                            ? `${row.batteryAmp} Ah`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" ") || "-"}
                        {row.batteryExpDate
                          ? ` (exp ${moment(row.batteryExpDate).format(
                              "DD-MM-YYYY",
                            )})`
                          : ""}
                      </TableCell>
                      <TableCell sx={this.bodyCellSx}>
                        {this.formatBool(row.dtcEngine)}
                      </TableCell>
                      <TableCell sx={this.bodyCellSx}>
                        {this.formatBool(row.dtcEmission)}
                      </TableCell>
                      <TableCell sx={this.bodyCellSx}>
                        {this.formatBool(row.dtcBrakes)}
                      </TableCell>
                      <TableCell align="center" sx={this.bodyCellSx}>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.75,
                          }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<VisibilityIcon fontSize="small" />}
                            onClick={() => this.openViewAssessment(row)}
                            sx={{
                              textTransform: "none",
                              whiteSpace: "nowrap",
                              borderColor: ENTERPRISE.border,
                              color: ENTERPRISE.accent,
                              borderRadius: "6px",
                              fontWeight: 600,
                              "&:hover": {
                                borderColor: ENTERPRISE.accent,
                                backgroundColor: "rgba(21,101,192,0.04)",
                              },
                            }}
                          >
                            View
                          </Button>
                          {isSuperAdmin && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={
                                <DeleteOutlineIcon fontSize="small" />
                              }
                              onClick={() =>
                                this.deleteVehicleAssessment(row)
                              }
                              disabled={
                                this.state.assessmentLoading || !row?.id
                              }
                              sx={{
                                textTransform: "none",
                                whiteSpace: "nowrap",
                                borderRadius: "6px",
                                fontWeight: 600,
                              }}
                            >
                              Delete
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
    );
  }
}
